import { useEffect, useRef, useState } from 'react'
import { useStorage } from 'hooks/auth'
import { resolveStorageUrl } from 'util/storageUrl'
import { captureException } from '@sentry/react'

/** A same-origin URL lets native video tracks load VTT without storage CORS headers. */
export function useVttBlobUrl(vttSrc: string | undefined) {
  const storage = useStorage()
  const activeUrl = useRef<string | undefined>(undefined)
  const [loaded, setLoaded] = useState<{
    source: string
    url: string
  }>()

  useEffect(() => {
    if (!vttSrc) return
    let cancelled = false
    let objectUrl: string | undefined

    resolveStorageUrl(storage, vttSrc)
      .then((url) => fetch(url))
      .then((response) => {
        if (response.status === 404) return undefined
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.blob()
      })
      .then((blob) => {
        if (cancelled || !blob) return
        objectUrl = URL.createObjectURL(blob)
        activeUrl.current = objectUrl
        setLoaded({ source: vttSrc, url: objectUrl })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          error.code === 'storage/object-not-found'
        )
          return
        captureException(error, { tags: { source: 'video_vtt' } })
      })

    return () => {
      cancelled = true
      if (objectUrl) {
        if (activeUrl.current === objectUrl) activeUrl.current = undefined
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [storage, vttSrc])

  return loaded && loaded.source === vttSrc && loaded.url === activeUrl.current
    ? loaded.url
    : undefined
}
